// frontend/src/App.tsx
import React, { useEffect, useState, useRef, Fragment } from 'react';
// --- Minimal custom route handler for /posts/:id etc ---
function parsePathForDeepLink() {
  const path = window.location.pathname;
  // Supported: /posts/:id, /events/:id, /services/:id, /products/:id, /venues/:id
  const match = path.match(/^\/(posts|events|services|products|venues)\/(\w+)/);
  if (match) {
    const kind = match[1];
    const id = match[2];
    // Map to view/tab and query param
    if (kind === 'posts') {
      return { view: 'posts', tab: 'posts', post: id };
    } else if (kind === 'events') {
      return { view: 'posts', tab: 'events', post: id };
    } else if (kind === 'services') {
      return { view: 'posts', tab: 'services', post: id };
    } else if (kind === 'products') {
      return { view: 'posts', tab: 'products', post: id };
    } else if (kind === 'venues') {
      return { view: 'posts', tab: 'events', post: id };
    }
  }
  return null;
}
import { socket } from './socket';
import axios from 'axios';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import { Send, Trash2, X, Zap, Settings, MoreVertical, Edit } from 'lucide-react';
import tkLogo from './assets/teakonn-logo.png';

import Login from './pages/Login';
import Register from './pages/Register';
import StatusPicker from './components/StatusPicker';
import SearchUsers from './components/SearchUsers';
import ConversationsList from './components/ConversationsList';
import GroupChatsList from './components/GroupChatsList';
import AllUsers from './pages/AllUsersModern';
import FollowersList from './pages/FollowersList';
import FollowingList from './pages/FollowingList';
import Discover from './pages/Discover';
import ErrorBoundary from './components/ErrorBoundary';
import DiscoverVenues from './pages/DiscoverVenues';
import SportsEvents from './pages/SportsEvents';
import Dashboard from './pages/Dashboard';
import VerificationRequest from './pages/VerificationRequest';
import AdminVerificationRequests from './pages/AdminVerificationRequests';
import Notifications from './pages/Notifications';
import MyEvents from './pages/MyEvents';
import MyActivities from './pages/MyActivities';
import MyServices from './pages/MyServices';
import MyProducts from './pages/MyProducts';
import PastEvents from './pages/PastEvents';
import OtherEvents from './pages/OtherEvents';
import Posts from './pages/Posts';
import UserContent from './pages/UserContent';
import AdminDashboard from './pages/AdminDashboard';
import AdminStats from './pages/AdminStats';
import AdminUsers from './pages/AdminUsers';
import AdminFlags from './pages/AdminFlags';
import AdminPosts from './pages/AdminPosts';
import Avatar from './components/Avatar';
import Sidebar from './components/Sidebar';
import AssistantWidget from './components/AssistantWidget';
import { API_URL } from './config/api';
import { conversationRoomId, normalizeConversation } from './utils/conversation';
import UserProfileModal from './components/UserProfileModal';
import ProfileEditModal from './components/ProfileEditModal';
import NotificationToast from './components/NotificationToast';
import OnboardingHints from './components/OnboardingHints';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.updateLocale('en', { weekStart: 1 });

// Use centralized normalized API base
const API = API_URL.replace(/\/api$/, '');

const SAMPLE_AVATAR = 'https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff';

const THEME_KEY = 'teakonn-theme';

function getInitialTheme() {
  if (typeof window === 'undefined') return 'dark';
  return localStorage.getItem(THEME_KEY) || 'dark';
}

type User = {
  _id?: string;
  id?: string;
  username?: string;
  email?: string;
  avatar?: string;
  role?: string;
  followers?: string[] | number;
  following?: string[] | number;
};

export default function App() {
  const [theme, setTheme] = useState<string>(getInitialTheme());
  const ASSISTANT_ENABLED = (import.meta.env.VITE_ASSISTANT_ENABLED === 'true');

  useEffect(() => {
    // Add smooth transition class
    document.documentElement.style.setProperty('--theme-transition', '0.3s');

    // Update theme class
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);

    // Persist theme
    localStorage.setItem(THEME_KEY, theme);

    // Log for debugging
    console.log(`Theme switched to: ${theme}`);
  }, [theme]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  // AUTH --------------------------------------
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  });
  const [authPage, setAuthPage] = useState<'login' | 'register'>(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const fromUrl = qs.get('auth');
      if (fromUrl === 'register' || fromUrl === 'login') return fromUrl as any;
      const saved = localStorage.getItem('auralink-auth-page');
      return saved === 'register' ? 'register' : 'login';
    } catch {
      return 'login';
    }
  });

  // Persist auth page and sync to URL so refresh keeps current page
  useEffect(() => {
    try {
      localStorage.setItem('auralink-auth-page', authPage);
      const params = new URLSearchParams(window.location.search);
      params.set('auth', authPage);
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      window.history.replaceState({ ...(window.history.state || {}), authPage }, '', newUrl);
    } catch (e) {
      console.warn('Failed to persist auth page to URL/localStorage:', e);
    }
  }, [authPage]);

  // DM --------------------------------
  const [room, setRoom] = useState<string>('general');
  const [messages, setMessages] = useState<any[]>([]);
  const [text, setText] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Record<string, any>>({});
  const [roomTitle, setRoomTitle] = useState<string>('');
  const [roomSubtitle, setRoomSubtitle] = useState<string>('');
  const [toast, setToast] = useState<
    | { message: string; type: 'success' | 'error' | 'info' | 'warning' }
    | null
  >(null);

  function bumpNotifUnread(delta = 1) {
    try {
      const raw = localStorage.getItem('auralink-unread-notifs') || '0';
      const n = parseInt(raw, 10) || 0;
      const next = Math.max(0, n + delta);
      localStorage.setItem('auralink-unread-notifs', String(next));
      window.dispatchEvent(
        new CustomEvent('auralink-unread-notifs.update', { detail: { count: next } })
      );
    } catch {}
  }

  // STATUS ------------------------------------
  const [statuses, setStatuses] = useState<Record<string, any>>({});

  // ONLINE USERS ------------------------------
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  // UI refs
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesSectionRef = useRef<HTMLDivElement | null>(null);
  const unreadRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});
  const [showChatActions, setShowChatActions] = useState(false);
  // DM / Conversations state
  const [inDM, setInDM] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem('auralink-in-dm');
      return raw ? JSON.parse(raw) : false;
    } catch {
      return false;
    }
  });
  const [activeConversation, setActiveConversation] = useState<any | null>(() => {
    try {
      const raw = localStorage.getItem('auralink-active-conversation');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [conversations, setConversations] = useState<any[]>([]);
  // Long-press hint removed; long-press selection is no longer supported

  // History guard to prevent leaving web to native app on Back
  const historyCountRef = useRef<number>(0);
  function getHistoryCount(): number {
    try {
      const raw = sessionStorage.getItem('auralink-history-count') || '0';
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
      return historyCountRef.current || 0;
    }
  }
  function setHistoryCount(n: number) {
    historyCountRef.current = n;
    try {
      sessionStorage.setItem('auralink-history-count', String(n));
    } catch {}
  }
  // Initialize a baseline history count so the first Back stays in-app
  useEffect(() => {
    const current = getHistoryCount();
    if (current <= 0) setHistoryCount(1);
    // Push a pinned state entry so Back immediately loops within the web app
    try {
      const params = new URLSearchParams(window.location.search);
      const qs = params.toString();
      const href = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      window.history.pushState({ pinned: true }, '', href);
      setHistoryCount(getHistoryCount() + 1);
    } catch {}
  }, []);

  // startHeaderPress/cancelHeaderPress removed

  // Persist DM state
  useEffect(() => {
    localStorage.setItem('auralink-in-dm', JSON.stringify(inDM));
  }, [inDM]);

  useEffect(() => {
    if (activeConversation) {
      localStorage.setItem('auralink-active-conversation', JSON.stringify(activeConversation));
    } else {
      localStorage.removeItem('auralink-active-conversation');
    }
  }, [activeConversation]);

  // dynamic pages
  const [view, setView] = useState<
    | 'dashboard'
    | 'discover'
    | 'chat'
    | 'all-users'
    | 'followers'
    | 'following'
    | 'posts'
    | 'direct-messages'
    | 'group-chats'
    | 'user-content'
    | 'past-events'
    | 'notifications'
    | 'my-activities'
    | 'my-events'
    | 'my-services'
    | 'my-products'
    | 'admin'
    | 'admin-stats'
    | 'admin-users'
    | 'admin-flags'
    | 'admin-posts'
    | 'verify-request'
    | 'admin-verifications'
  >(() => {
    // Restore previous view from localStorage
    const saved = localStorage.getItem('auralink-current-view');
    return (saved as any) || 'posts';
  });

  // Persist view changes to localStorage
  useEffect(() => {
    localStorage.setItem('auralink-current-view', view);
  }, [view]);

  // Handle event chat deep-link from MyEvents (auralink-open-room)
  useEffect(() => {
    if (view !== 'chat') return;
    try {
      const rid = localStorage.getItem('auralink-open-room');
      if (rid) {
        setInDM(false);
        setActiveConversation(null);
        setRoom(rid);
        localStorage.removeItem('auralink-open-room');
        setTimeout(() => scrollToBottom(), 200);
      }
    } catch {}
  }, [view]);

  // Keep URL query in sync with current view to preserve on refresh
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('view', view);
      if (view !== 'posts') params.delete('post');
      // Ensure Posts tab param exists on initial load if missing, alternating each session
      if (view === 'posts') {
        const currentTab = params.get('tab');
        if (!currentTab) {
          try {
            const last = localStorage.getItem('auralink-last-feed-tab');
            const next = last === 'events' ? 'posts' : 'events';
            params.set('tab', next);
            localStorage.setItem('auralink-last-feed-tab', next);
          } catch {}
        }
      } else {
        params.delete('tab');
      }
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      window.history.replaceState({ view }, '', newUrl);
    } catch (e) {
      console.warn('Failed to sync view to URL:', e);
    }
  }, [view]);

  // Centralized navigation: push history entry and update view
  function navigateTo(newView: typeof view) {
    setView(newView);
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('view', newView);
      if (newView !== 'posts') params.delete('post');
      // When navigating to posts explicitly, set tab if missing and alternate from last
      if (newView === 'posts') {
        const currentTab = params.get('tab');
        if (!currentTab) {
          try {
            const last = localStorage.getItem('auralink-last-feed-tab');
            const next = last === 'events' ? 'posts' : 'events';
            params.set('tab', next);
            localStorage.setItem('auralink-last-feed-tab', next);
          } catch {}
        }
      } else {
        params.delete('tab');
      }
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      window.history.pushState({ view: newView }, '', newUrl);
      // Track in-session history depth so Back never leaves the web app
      setHistoryCount(getHistoryCount() + 1);
    } catch (e) {
      console.warn('Failed to push navigation state:', e);
    }
  }

  // Handle browser back/forward: update view from history state or URL
  useEffect(() => {
    const onPop = () => {
      try {
        // Decrement and guard against leaving the site (native app)
        const nextDepth = Math.max(0, getHistoryCount() - 1);
        setHistoryCount(nextDepth);
        if (nextDepth === 0) {
          // Re-push current state to remain on web and restore depth baseline
          const params = new URLSearchParams(window.location.search);
          const qs = params.toString();
          const href = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
          window.history.pushState({ view }, '', href);
          setHistoryCount(1);
          return;
        }
        const stateView = (window.history.state && window.history.state.view) || null;
        if (stateView) {
          setView(stateView as any);
          return;
        }
        const params = new URLSearchParams(window.location.search);
        const v = params.get('view');
        if (v) setView(v as any);
      } catch (e) {
        console.warn('popstate handler failed:', e);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Handle deep-link query params and custom path routes (e.g., /posts/:id)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get('view');
      const postId = params.get('post');
      // Minimal custom route handler for /posts/:id etc
      const deepLink = parsePathForDeepLink();
      if (deepLink) {
        // Set view and tab
        setView(deepLink.view as any);
        // Set tab in URL
        params.set('tab', deepLink.tab);
        // Set post id in localStorage for highlight
        if (deepLink.post) {
          localStorage.setItem('auralink-highlight-post', deepLink.post);
          params.set('post', deepLink.post);
        }
        // Replace URL with query params (so app can handle as usual)
        const qs = params.toString();
        window.history.replaceState({ view: deepLink.view }, '', `/?${qs}`);
        return;
      }
      // Email verification deep link: /verify-email?token=...
      const path = window.location.pathname || '';
      const verifyToken = params.get('token');
      const resetToken = verifyToken; // reuse token param for reset-password route
      if (path.endsWith('/verify-email') && verifyToken) {
        // Attempt verification via API
        (async () => {
          try {
            await axios.post(API + '/api/auth/verify-email', { token: verifyToken });
            setToast({ message: 'Email verified successfully. Welcome!', type: 'success' });
            // Update local user state
            setUser((prev) => {
              if (!prev) return prev;
              const next = { ...prev, emailVerified: true } as any;
              try { localStorage.setItem('user', JSON.stringify(next)); } catch {}
              return next;
            });
          } catch (e) {
            setToast({ message: 'Verification link invalid or expired.', type: 'error' });
          } finally {
            // Clean URL back to root
            try {
              const url = new URL(window.location.href);
              url.pathname = '/';
              url.searchParams.delete('token');
              window.history.replaceState({ view: 'dashboard' }, '', url.toString());
            } catch {}
          }
        })();
      }
      // Password reset deep link: /reset-password?token=...
      if (path.endsWith('/reset-password') && resetToken) {
        setAuthPage('login');
        // Show inline prompt to set new password
        setToast({ message: 'Enter a new password to reset your account.', type: 'info' });
        try {
          // Replace URL back to root after a short delay
          setTimeout(() => {
            const url = new URL(window.location.href);
            url.pathname = '/';
            url.searchParams.delete('token');
            window.history.replaceState({ view: 'dashboard' }, '', url.toString());
          }, 2000);
        } catch {}
        // Store token temporarily for Login to detect and render reset UI if desired
        try { localStorage.setItem('auralink-reset-token', resetToken); } catch {}
      }
      if (v) setView(v as any);
      if (postId) localStorage.setItem('auralink-highlight-post', postId);
    } catch (e) {
      console.warn('Deep link parsing failed:', e);
    }
  }, []);


  // editing messages
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState<string>('');

  // PROFILE MODAL -----------------------------
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileFollowersCount, setProfileFollowersCount] = useState<number | null>(null);
  const [profileFollowingCount, setProfileFollowingCount] = useState<number | null>(null);
  const [profileIsFollowed, setProfileIsFollowed] = useState<boolean>(false);

  // DM collapse
  const [dmOpen, setDmOpen] = useState<boolean>(true);

  // Missing states and helpers added
  // Sidebar avatar upload removed; handled in Edit Profile modal
  const [unreadIndex, setUnreadIndex] = useState<number>(-1);
  const [enterToSend, setEnterToSend] = useState<boolean>(() => {
    const saved = localStorage.getItem('enterToSend');
    return saved ? JSON.parse(saved) : false;
  });

  // Conversation refresh key
  const [conversationRefreshKey, setConversationRefreshKey] = useState<number>(0);
  const refreshConversations = () => setConversationRefreshKey((k) => k + 1);

  // Broadcast immediate DM read to other components and sync cached list
  function broadcastConversationRead(convId: string) {
    try {
      window.dispatchEvent(
        new CustomEvent('auralink-conversation.read', { detail: { conversationId: convId } }),
      );
    } catch {}
    try {
      const raw = localStorage.getItem('auralink-conversations-cache') || '';
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed?.data)
        ? parsed.data
        : Array.isArray(parsed)
          ? parsed
          : [];
      const next = list.map((c: any) =>
        String(c._id) === String(convId) ? { ...c, unreadCount: 0 } : c,
      );
      localStorage.setItem(
        'auralink-conversations-cache',
        JSON.stringify({ ts: Date.now(), data: next }),
      );
    } catch {}
  }

  // Per-message actions dropdown (collapsed by default)
  const [openMessageActions, setOpenMessageActions] = useState<string | null>(null);
  const [messageSelectMode, setMessageSelectMode] = useState<boolean>(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Record<string, boolean>>({});
  const AVAILABLE_REACTIONS = ['❤️', '🔥', '😂', '😔'];
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [replyTo, setReplyTo] = useState<any | null>(null);
  const [openReactionPicker, setOpenReactionPicker] = useState<Record<string, boolean>>({});
  // Onboarding (one-time hints for newly registered users)
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  // Track pending deletions to suppress later real message reconciliation (e.g., photos)
  const suppressedPendingRef = useRef<Array<{ sender: string; ts: number }>>([]);

  // Check onboarding eligibility once user is available
  useEffect(() => {
    try {
      if (!user || !(user._id || user.id)) {
        setShowOnboarding(false);
        return;
      }
      const uid = String(user._id || user.id);
      const dismissed = localStorage.getItem(`auralink-onboard-dismissed:${uid}`) === 'true';
      const eligible = localStorage.getItem(`auralink-onboard-eligible:${uid}`) === 'true';
      setShowOnboarding(Boolean(eligible && !dismissed));
    } catch {
      setShowOnboarding(false);
    }
  }, [user]);

  // Long-press message handlers removed in favor of a three-dots menu

  function shouldShowAvatar(index: number) {
    if (index === 0) return true;
    return messages[index - 1]?.sender?._id !== messages[index]?.sender?._id;
  }
  // Group consecutive messages by the same sender so they render as one trail
  function groupConsecutiveMessages(list: any[]) {
    const groups: Array<{ senderId: string; items: any[] }> = [];
    for (const m of list) {
      const sid = String(m.sender?._id || m.sender?.id || '');
      const last = groups[groups.length - 1];
      if (last && String(last.senderId) === sid) {
        last.items.push(m);
      } else {
        groups.push({ senderId: sid, items: [m] });
      }
    }
    return groups;
  }

  function toggleEnterToSend() {
    const newValue = !enterToSend;
    setEnterToSend(newValue);
    localStorage.setItem('enterToSend', JSON.stringify(newValue));
  }

  function jumpToUnread() {
    if (unreadRef.current) unreadRef.current.scrollIntoView({ behavior: 'smooth' });
  }

  function startEdit(m: any) {
    setEditingMessageId(m._id);
    setEditingText(m.text || '');
  }

  function cancelEdit() {
    setEditingMessageId(null);
    setEditingText('');
  }

  // Long-press selection removed

  function onMessageContainerClick(e: React.MouseEvent<HTMLDivElement>, msgId?: string) {
    const t = e.target as HTMLElement;
    const tag = (t.tagName || '').toLowerCase();
    if (['button', 'a', 'input', 'textarea', 'img', 'svg', 'path'].includes(tag)) return;
    if (messageSelectMode) return;
    composerTextareaRef.current?.focus();
  }

  // Long-press selection removed; use the explicit Select toggle instead

  function toggleMessageSelected(id: string) {
    setSelectedMessageIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }
  function selectAllMessages() {
    const next: Record<string, boolean> = {};
    for (const m of messages) {
      next[String(m._id)] = true;
    }
    setSelectedMessageIds(next);
  }
  function clearSelectedMessages() {
    setSelectedMessageIds({});
  }
  async function deleteSelectedMessagesForMe() {
    const ids = Object.keys(selectedMessageIds).filter((k) => selectedMessageIds[k]);
    if (!ids.length) return;
    if (!confirm(`Hide ${ids.length} selected message(s) for you?`)) return;
    try {
      await Promise.all(
        ids.map((id) =>
          axios.delete(`${API}/api/messages/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ),
      );
      setMessages((msgs) => msgs.filter((m) => !ids.includes(String(m._id))));
      setMessageSelectMode(false);
      clearSelectedMessages();
    } catch (e) {
      console.error('Bulk hide messages failed', e);
      alert('Failed to hide selected messages');
    }
  }
  async function markAllRead() {
    try {
      if (!confirm('Mark all messages in this chat as read?')) return;
      const roomId = inDM && activeConversation ? conversationRoomId(activeConversation) : room;
      if (!roomId) return;
      await axios.post(`${API}/api/messages/rooms/${roomId}/mark-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (inDM && activeConversation) {
        await axios.post(`${API}/api/conversations/${conversationRoomId(activeConversation)}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      }
      setUnreadIndex(-1);
    } catch (e) {
      console.error('Mark all read failed', e);
      alert('Failed to mark as read');
    }
  }
  async function markConversationUnread() {
    try {
      if (!confirm('Mark this conversation as unread?')) return;
      if (inDM && activeConversation) {
        await axios.post(`${API}/api/conversations/${conversationRoomId(activeConversation)}/unread`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setUnreadIndex(0);
        try { refreshConversations(); } catch {}
      }
    } catch (e) {
      console.error('Mark conversation unread failed', e);
      alert('Failed to mark conversation as unread');
    }
  }

  async function saveEdit(id: string) {
    if (!token) return;

    // Optimistic update - update message immediately
    setMessages((m) =>
      m.map((msg) => (msg._id === id ? { ...msg, text: editingText, edited: true } : msg)),
    );
    setEditingMessageId(null);

    try {
      await axios.put(
        API + '/api/messages/' + id,
        { text: editingText },
        { headers: { Authorization: 'Bearer ' + token } },
      );
      socket.emit('edit_message', { messageId: id, text: editingText });
    } catch (e) {
      console.error('Edit failed', e);
      // Could revert the edit here if it failed
    }
  }

  async function deleteMessage(id: string) {
    if (!token) return;

    // Optimistic update - remove message immediately from my view
    setMessages((m) => m.filter((x) => x._id !== id));

    try {
      // REST endpoint handles per-user hiding and sends socket event only to me
      await axios.delete(API + '/api/messages/' + id, {
        headers: { Authorization: 'Bearer ' + token },
      });
    } catch (e) {
      console.error('Delete failed', e);
      // Could reload messages here if delete failed
    }
  }

  function onMyStatusUpdated(newStatus: any) {
    const uid = String(user?._id || user?.id);
    console.log('App: onMyStatusUpdated called with:', newStatus, 'for user:', uid);
    setStatuses((s) => {
      const updated = { ...s };
      if (newStatus === null) {
        delete updated[uid];
        console.log('App: Cleared status for user:', uid);
      } else {
        updated[uid] = newStatus;
        console.log('App: Updated status for user:', uid, newStatus);
      }
      return updated;
    });
  }

  const myStatus = statuses[String(user?._id || user?.id)] || null;

  function makeAvatarUrl(avatar?: string | null) {
    if (!avatar) return SAMPLE_AVATAR;
    if (avatar.startsWith('http')) return avatar;
    if (avatar.startsWith('/')) return API + avatar;
    return API + '/uploads/' + avatar;
  }

  // SOCKET SETUP -------------------------------------------------
  useEffect(() => {
    if (!token || !user) return;

    socket.auth = { token, user };
    socket.connect();

    // Join initial room
    const initialRoom = inDM && activeConversation ? conversationRoomId(activeConversation) : room;
    socket.emit('join_room', initialRoom);
    console.log('[Socket] Joined room:', initialRoom);

    socket.on('temp_message', (msg: any) => {
      try {
        // Ignore our own temp messages (we already added optimistic locally)
        const fromMe = String(msg?.sender?._id || msg?.sender) === String(user?._id);
        if (fromMe) return;
      } catch {}
      setMessages((m) => {
        // Avoid duplicates by temp _id
        const exists = m.some((x) => x._id === msg._id);
        if (exists) return m;
        const next = [...m, msg];
        computeUnreadIndex(next);
        return next;
      });
      // Unread increments handled in receive handler for consistency
    });

    socket.on('receive_message', async (msg: any) => {
      // Suppress real message if a pending placeholder with same sender+timestamp was deleted
      try {
        const senderId = String(msg?.sender?._id || msg?.sender || '');
        const ts = new Date(msg?.createdAt || Date.now()).getTime();
        const suppressed = suppressedPendingRef.current.some(
          (e) => e.sender === senderId && Math.abs(e.ts - ts) < 5000,
        );
        if (suppressed) {
          // Drop this incoming message; clear matched suppression entry
          suppressedPendingRef.current = suppressedPendingRef.current.filter(
            (e) => !(e.sender === senderId && Math.abs(e.ts - ts) < 5000),
          );
          return;
        }
      } catch {}
      setMessages((m) => {
        // Remove any pending/optimistic messages from the same user with similar timestamp
        const filtered = m.filter((existing) => {
          if (!existing.isPending) return true;
          // Remove pending message if we received the real one
          if (
            existing.sender?._id === msg.sender?._id &&
            existing.text === msg.text &&
            Math.abs(new Date(existing.createdAt).getTime() - new Date(msg.createdAt).getTime()) <
              5000
          ) {
            return false;
          }
          return true;
        });

        // Check if message already exists (prevent duplicates)
        const exists = filtered.some((existing) => existing._id === msg._id);
        if (exists) return filtered;

        const next = [...filtered, msg];
        computeUnreadIndex(next);
        return next;
      });
      scrollToBottom();

      // If this is a DM, update unread counts locally for immediacy
      try {
        const convId = (msg as any).conversationId || (msg as any).room;
        if (convId) {
          const isActive = inDM && String(conversationRoomId(activeConversation)) === String(convId);
          const isFromMe =
            String(msg?.sender?._id || msg?.sender?.id || msg?.sender) ===
            String(user?._id || user?.id);
          setConversations((list) => {
            if (!Array.isArray(list)) return list;
            return list.map((c: any) => {
              if (String(conversationRoomId(c)) !== String(convId)) return c;
              // If it's the active DM or message is from me, keep unread at 0
              if (isActive || isFromMe) return { ...c, unreadCount: 0 };
              const prev = Number(c.unreadCount || 0);
              return { ...c, unreadCount: prev + 1 };
            });
          });
          // If active chat, also mark as read immediately (server call already sent above)
          if (isActive) {
            try { broadcastConversationRead(String(convId)); } catch {}
            setConversations((list) =>
              Array.isArray(list)
                ? list.map((c: any) =>
                    String(conversationRoomId(c)) === String(convId) ? { ...c, unreadCount: 0 } : c,
                  )
                : list,
            );
          }
        }
      } catch {}

      // Mark as delivered if not sender
      if (msg.sender?._id !== user?._id) {
        socket.emit('message_delivered', { messageId: msg._id, userId: user?._id });

        // Also mark as read immediately if chat is visible (view === "chat")
        if (view === 'chat') {
          socket.emit('message_read', { messageId: msg._id, userId: user?._id });

          // Mark conversation as read if it's a DM and we're viewing it
          if (inDM && conversationRoomId(activeConversation) && token) {
            try {
              try { socket.emit('mark_conversation_read', { conversationId: conversationRoomId(activeConversation) }); } catch {}
              await axios.post(
                `${API}/api/conversations/${conversationRoomId(activeConversation)}/read`,
                {},
                { headers: { Authorization: 'Bearer ' + token } },
              );
              // Refresh conversations so sidebar unread clears immediately
              refreshConversations();
            } catch (err) {
              console.error('Failed to mark conversation as read:', err);
            }
          }
        }

        // Show browser notification for messages from others (only if not currently viewing chat)
        if (view !== 'chat' && 'Notification' in window && Notification.permission === 'granted') {
          const senderName = msg.sender?.username || 'Someone';
          const messageText = msg.text || (msg.fileUrl ? 'Sent an image' : 'New message');
          const notification = new Notification(`${senderName}`, {
            body: messageText.substring(0, 100),
            icon: msg.sender?.avatar
              ? msg.sender.avatar.startsWith('http')
                ? msg.sender.avatar
                : API + '/uploads/' + msg.sender.avatar
              : SAMPLE_AVATAR,
            tag: msg._id,
            requireInteraction: false,
          });

          // Auto-close after 5 seconds
          setTimeout(() => notification.close(), 5000);

          // Focus window when clicking notification
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }
      }
    });

    // Update DM unread counts immediately on read events (no refetch)
    socket.on('conversation_read', (payload: any) => {
      try {
        const convId = payload?.conversationId;
        if (!convId) return;
        setConversations((list) => Array.isArray(list) ? list.map((c: any) => (String(c._id) === String(convId) ? { ...c, unreadCount: 0 } : c)) : list);
      } catch {}
    });

    socket.on('message_status_update', (updatedMsg: any) => {
      setMessages((m) => {
        const next = m.map((x) => (x._id === updatedMsg._id ? updatedMsg : x));
        computeUnreadIndex(next);
        return next;
      });
    });

    socket.on('reaction_update', (msg: any) => {
      setMessages((m) => m.map((x) => (x._id === msg._id ? msg : x)));
    });

    socket.on('typing', ({ userId, typing, user: typingUser, conversationId }: any) => {
      console.log('[Socket] Typing event:', { userId, typing, user: typingUser });
      // Only show typing indicator for the active DM/conversation
      if (conversationId && inDM && String(conversationRoomId(activeConversation)) !== String(conversationId)) {
        return;
      }
      if (typing) {
        setTypingUsers((t) => {
          const updated = { ...t, [userId]: typingUser || { username: 'Someone' } };
          console.log('[Socket] Typing users updated:', updated);
          return updated;
        });

        // Clear typing indicator after 3 seconds of inactivity
        setTimeout(() => {
          setTypingUsers((t) => {
            const updated = { ...t };
            delete updated[userId];
            return updated;
          });
        }, 3000);
      } else {
        setTypingUsers((t) => {
          const updated = { ...t };
          delete updated[userId];
          return updated;
        });
      }
    });

    socket.on('message_edited', (updatedMsg: any) => {
      setMessages((m) => m.map((x) => (x._id === updatedMsg._id ? updatedMsg : x)));
    });

    socket.on('message_deleted', (payload: any) => {
      const messageId = typeof payload === 'string' ? payload : payload?.id;
      if (!messageId) return;
      setMessages((m) => m.filter((x) => x._id !== messageId));
      const reason = typeof payload === 'object' ? payload?.reason : null;
      if (reason === 'organizer_delete') {
        setToast({ message: 'A message was removed by the organizer.', type: 'warning' });
      }
    });

    socket.on('messages_bulk_deleted', ({ ids, reason }: { ids: string[]; reason?: string }) => {
      if (Array.isArray(ids) && ids.length > 0) {
        setMessages((m) => m.filter((x) => !ids.includes(String(x._id))));
        if (reason === 'participant_removed') {
          setToast({ message: 'Removed participant’s messages were cleared.', type: 'info' });
        }
      }
    });

    socket.on('participant_removed', ({ userId }: any) => {
      setToast({ message: 'A participant was removed by the organizer.', type: 'warning' });
    });

    socket.on('message_hidden', ({ messageId }: { messageId: string }) => {
      // Only remove from my view when I hide a message
      setMessages((m) => m.filter((x) => x._id !== messageId));
    });

    socket.on('status_update', (payload: any) => {
      if (payload?.cleared) {
        setStatuses((s) => {
          const c = { ...s };
          delete c[payload.user];
          return c;
        });
      } else if (payload?.user) {
        const uid = String(payload.user._id || payload.user);
        setStatuses((s) => ({ ...s, [uid]: payload }));
      }
    });

    socket.on(
      'presence_update',
      ({ userId, status }: { userId: string; status: 'online' | 'offline' }) => {
        console.log('[Socket] Presence update:', userId, status);
        setOnlineUsers((prev) => {
          const updated = new Set(prev);
          if (status === 'online') {
            updated.add(userId);
          } else {
            updated.delete(userId);
          }
          console.log('[Socket] Online users:', Array.from(updated));
          return updated;
        });
      },
    );

    // Receive initial online users list when connecting
    socket.on('online_users_list', ({ userIds }: { userIds: string[] }) => {
      console.log('[Socket] Received online users list:', userIds);
      setOnlineUsers(new Set(userIds));
    });

    // Admin-enforced logout: clear client state immediately
    socket.on('force_logout', ({ reason }: any) => {
      setToast({ message: 'Your account was removed by an admin. You have been logged out.', type: 'warning' });
      logout();
    });

    socket.on('room_join_denied', ({ room, reason }: any) => {
      console.warn('[Socket] room_join_denied:', { room, reason });
      const message =
        reason === 'not_participant'
          ? 'You are not a participant of this event.'
          : 'Unable to join event chat.';
      setToast({ message, type: 'error' });
      try { localStorage.removeItem('auralink-open-room'); } catch {}
      // Bounce the user back to My Events and reset to a safe room
      setInDM(false);
      setActiveConversation(null);
      setRoom('general');
      navigateTo('my-events');
    });

    // Join request notifications
    socket.on('join_request_created', ({ eventTitle, organizerId }: any) => {
      if (user?._id === organizerId) {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('New Join Request', {
            body: `Someone requested to join your event: ${eventTitle}`,
            icon: '/logo.png',
          });
        }
      }
    });

    socket.on('join_request_approved', ({ eventTitle, userId }: any) => {
      if (user?._id === userId) {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Join Request Approved', {
            body: `Your request to join "${eventTitle}" has been approved!`,
            icon: '/logo.png',
          });
        }
      }
    });

    socket.on('join_request_rejected', ({ eventTitle, userId }: any) => {
      if (user?._id === userId) {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Join Request Rejected', {
            body: `Your request to join "${eventTitle}" was not approved.`,
            icon: '/logo.png',
          });
        }
      }
    });

    // Booking status update notifications
    socket.on(
      'booking_status_update',
      ({ bookingId, status, approvalStatus, message, paymentVerified }: any) => {
        console.log('[Socket] Booking status update:', { bookingId, status, message });

        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Booking Update', {
            body: message,
            icon: '/logo.png',
          });
        }

        // Show in-app alert (optional - could be a toast notification instead)
        alert(message);
      },
    );

    // Global notification listener: booking_received, booking_token, follow
    // Notifications helpers: seen tracking + badge updates
    function readSeenSet(): string[] {
      try {
        const raw = localStorage.getItem('auralink-notifs-seen') || '[]';
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
      } catch {
        return [];
      }
    }
    function writeSeenSet(setArr: string[]) {
      try { localStorage.setItem('auralink-notifs-seen', JSON.stringify(setArr)); } catch {}
    }
    function hasSeen(key: string): boolean {
      const arr = readSeenSet();
      return arr.includes(key);
    }
    function markSeen(key: string) {
      const arr = readSeenSet();
      if (!arr.includes(key)) {
        arr.push(key);
        writeSeenSet(arr);
      }
    }
    function bumpNotifUnread(delta = 1) {
      try {
        const raw = localStorage.getItem('auralink-unread-notifs') || '0';
        const n = parseInt(raw, 10) || 0;
        const next = Math.max(0, n + delta);
        localStorage.setItem('auralink-unread-notifs', String(next));
        window.dispatchEvent(
          new CustomEvent('auralink-unread-notifs.update', { detail: { count: next } })
        );
      } catch {}
    }
    socket.on('notification', (payload: any) => {
      try {
        if (!payload || !payload.kind) return;
        // Build a stable seen key when possible
        const today = new Date();
        const dayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
        let seenKey = `kind:${payload.kind}|date:${dayKey}`;
        if (payload.kind === 'follow') {
          const uid = String(payload.user?._id || payload.user?.id || payload.user?.username || 'anon');
          // Persistently seen per follower — do not re-notify across days
          seenKey = `follow:${uid}`;
        } else if (payload.kind === 'booking_received') {
          const vid = String(payload.venue?._id || payload.venue?.id || payload.venue?.name || 'venue');
          seenKey = `booking_received:${vid}|date:${dayKey}`;
        } else if (payload.kind === 'booking_token') {
          const vid = String(payload.venue?._id || payload.venue?.id || payload.venue?.name || 'venue');
          seenKey = `booking_token:${vid}|date:${dayKey}`;
        }

        // If already seen today, don't notify again
        if (hasSeen(seenKey)) return;

        // Otherwise show toast + bump badge and mark as seen
        if (payload.kind === 'booking_received') {
          setToast({ message: `New booking for ${payload.venue?.name || 'your venue'}`, type: 'info' });
          bumpNotifUnread(1);
          markSeen(seenKey);
        } else if (payload.kind === 'booking_token') {
          setToast({ message: `Response issued for ${payload.venue?.name || 'your request'}`, type: 'success' });
          bumpNotifUnread(1);
          markSeen(seenKey);
        } else if (payload.kind === 'follow') {
          setToast({ message: `${payload.user?.username || 'Someone'} followed you`, type: 'info' });
          bumpNotifUnread(1);
          // Persist latest follower details for sidebar display; do NOT mark seen yet
          try {
            const latest = {
              id: `follow:${String(payload.user?._id || payload.user?.id || payload.user?.username || 'anon')}`,
              username: String(payload.user?.username || 'Someone'),
              avatar: String(payload.user?.avatar || ''),
              at: Date.now(),
            };
            localStorage.setItem('auralink-last-follower', JSON.stringify(latest));
            // Notify any listeners (notifications page) for real-time reflection
            try {
              window.dispatchEvent(new CustomEvent('auralink-last-follower.update', { detail: latest }));
            } catch {}
          } catch {}
        }
      } catch (e) {
        console.warn('App: notification handler failed', e);
      }
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [token, user]);

  // Offline notifications check: on login, only bump for new followers and joined events due in a day
  useEffect(() => {
    if (!token || !user) return;
    (async () => {
      try {
        const headers = { Authorization: 'Bearer ' + token };
        const [meRes, eventsRes] = await Promise.all([
          axios.get(API + '/api/users/me', { headers }),
          axios.get(API + '/api/events?status=published&limit=200', { headers }),
        ]);

        const me = meRes.data || {};
        const myIdStr = String(me._id || me.id || '');
        const currentFollowerIds: string[] = Array.isArray(me.followers)
          ? me.followers.map((u: any) => String(u?._id || u))
          : [];
        // Compare against last-known follower snapshot to only count true additions
        const snapKey = `auralink-followers-snapshot:${myIdStr}`;
        let prevSnapshot: string[] = [];
        try { prevSnapshot = JSON.parse(localStorage.getItem(snapKey) || '[]'); } catch {}
        if (!Array.isArray(prevSnapshot)) prevSnapshot = [];
        const prevSet = new Set(prevSnapshot);
        const newFollowerIds = currentFollowerIds.filter((fid) => !prevSet.has(fid));
        const newFollowers = prevSnapshot.length === 0 ? 0 : newFollowerIds.length;
        // Update snapshot immediately so subsequent logins don't re-report old followers
        try { localStorage.setItem(snapKey, JSON.stringify(currentFollowerIds)); } catch {}

        const myId = String(me._id || me.id || '');
        const now = dayjs();
        const in24h = now.add(1, 'day');
        const joinedEventsDueSoon = (eventsRes.data?.events || []).filter((ev: any) => {
          const parts = Array.isArray(ev.participants) ? ev.participants : [];
          const joined = parts.some((p: any) => String(p?._id || p) === myId);
          const start = dayjs(ev.startDate);
          return joined && start.isAfter(now) && start.isBefore(in24h);
        });

        // Deduplicate per-day to avoid repeated overhead
        const registryKey = 'auralink-notified-events-due';
        let registry: string[] = [];
        try { registry = JSON.parse(localStorage.getItem(registryKey) || '[]'); } catch {}
        const dayKey = now.format('YYYY-MM-DD');
        const newlyDue = joinedEventsDueSoon.filter((ev: any) => !registry.includes(`${ev._id}|${dayKey}`));
        if (newlyDue.length > 0) {
          const nextReg = [...registry, ...newlyDue.map((ev: any) => `${ev._id}|${dayKey}`)];
          try { localStorage.setItem(registryKey, JSON.stringify(nextReg)); } catch {}
        }

        const eventsCount = newlyDue.length;
        const total = newFollowers + eventsCount;
        if (total > 0) {
          const parts: string[] = [];
          if (newFollowers > 0) parts.push(`${newFollowers} new follower${newFollowers > 1 ? 's' : ''}`);
          if (eventsCount > 0) parts.push(`${eventsCount} upcoming event${eventsCount > 1 ? 's' : ''} you joined due soon`);
          setToast({ message: `You have ${parts.join(' and ')}`, type: 'info' });
          bumpNotifUnread(total);

          // No additional ack needed; snapshot prevents repeats across sessions
        }
      } catch (e) {
        console.warn('Offline notifications check failed', e);
      }
    })();
  }, [token, user]);

  // Rejoin socket room when room or conversation changes
  useEffect(() => {
    if (!socket.connected) return;

    const targetRoom = inDM && activeConversation ? conversationRoomId(activeConversation) : room;
    console.log('[Socket] Switching to room:', targetRoom);
    socket.emit('join_room', targetRoom);
  }, [room, inDM, activeConversation]);

  // LOAD ROOM MESSAGES ------------------------------------------
  useEffect(() => {
    if (!token || inDM || view !== 'chat') return;

    setMessagesLoading(true);
    axios
      .get(API + '/api/messages/' + room, {
        headers: { Authorization: 'Bearer ' + token },
      })
      .then((r) => {
        const list = r.data || [];
        setMessages(list);
        computeUnreadIndex(list);
        // Auto-scroll to latest messages when opening the room
        scrollToBottom();

        // Mark messages as read when loading chat
        if (user && socket) {
          list.forEach((msg: any) => {
            if (
              msg.sender?._id !== user._id &&
              !msg.readBy?.some((id: any) => String(id) === String(user._id))
            ) {
              socket.emit('message_read', { messageId: msg._id, userId: user._id });
            }
          });
        }

        // Also mark the entire room as read via API to ensure counts clear
        // immediately (efficient batch update instead of per-message emits).
        try {
          axios.post(
            `${API}/api/messages/rooms/${room}/mark-read`,
            {},
            { headers: { Authorization: 'Bearer ' + token } }
          ).catch(() => {});
        } catch {}
      })
      .catch((e) => {
        try {
          const status = e?.response?.status;
          const data = e?.response?.data;
          const url = (e?.config?.baseURL || '') + (e?.config?.url || '');
          console.error('Room load error', { url, status, data });
        } catch {}
      })
      .finally(() => setMessagesLoading(false));
  }, [room, token, view, inDM, user, socket]);

  // Load room metadata (event title) when entering a non-DM room
  useEffect(() => {
    if (inDM || view !== 'chat') {
      setRoomTitle('');
      setRoomSubtitle('');
      return;
    }
    // Try to fetch event title; if not an event id, fall back to id itself
    let canceled = false;
    (async () => {
      try {
        if (!room || room === 'general') {
          if (!canceled) setRoomTitle('general');
          if (!canceled) setRoomSubtitle('');
          return;
        }
        const res = await axios.get(
          API + '/api/events/' + room + '?fields=title,startDate,location'
        );
        if (!canceled) {
          setRoomTitle(res.data?.title || '#' + room);
          const dateStr = res.data?.startDate
            ? dayjs(res.data.startDate).format('MMM D, YYYY')
            : '';
          const city = res.data?.location?.city || '';
          const name = res.data?.location?.name || '';
          const locParts = [name, city].filter(Boolean);
          const locStr = locParts.join(', ');
          setRoomSubtitle([dateStr, locStr].filter(Boolean).join(' • '));
        }
      } catch {
        if (!canceled) {
          setRoomTitle('#' + room);
          setRoomSubtitle('');
        }
      }
    })();
    return () => {
      canceled = true;
    };
  }, [room, inDM, view]);

  // LOAD DM MESSAGES --------------------------------------------
  useEffect(() => {
    if (!token || !inDM || !activeConversation) return;

    setMessagesLoading(true);
    axios
      .get(API + '/api/messages/' + conversationRoomId(activeConversation), {
        headers: { Authorization: 'Bearer ' + token },
      })
      .then((r) => {
        const list = r.data || [];
        setMessages(list);
        computeUnreadIndex(list);
        // Auto-scroll to latest messages when opening the conversation
        scrollToBottom();

        // Mark messages as read when loading DM conversation
        if (user && socket) {
          list.forEach((msg: any) => {
            if (
              msg.sender?._id !== user._id &&
              !msg.readBy?.some((id: any) => String(id) === String(user._id))
            ) {
              socket.emit('message_read', { messageId: msg._id, userId: user._id });
            }
          });
        }
      })
      .catch((e) => {
        try {
          const status = e?.response?.status;
          const data = e?.response?.data;
          const url = (e?.config?.baseURL || '') + (e?.config?.url || '');
          console.error('DM load error', { url, status, data });
        } catch {
          console.error('DM load error', e);
        }
      })
      .finally(() => setMessagesLoading(false));
  }, [token, inDM, activeConversation, user, socket]);

  // LOAD ALL STATUSES -------------------------------------------
  useEffect(() => {
    if (!token) return;

    axios
      .get(API + '/api/status', {
        headers: { Authorization: 'Bearer ' + token },
      })
      .then((r) => {
        const map: Record<string, any> = {};
        (r.data || []).forEach((st: any) => {
          const uid = String(st.user?._id || st.user);
          map[uid] = st;
        });
        setStatuses(map);
      })
      .catch(() => {});
  }, [token]);

  // LOAD USER CONVERSATIONS -------------------------------------
  useEffect(() => {
    if (!token) return;

    axios
      .get(API + '/api/conversations', {
        headers: { Authorization: 'Bearer ' + token },
      })
      .then((r) => setConversations((r.data || []).map(normalizeConversation)))
      .catch((e) => {
        try {
          const status = e?.response?.status;
          const data = e?.response?.data;
          const url = (e?.config?.baseURL || '') + (e?.config?.url || '');
          console.error('Conversations load error', { url, status, data });
        } catch {}
      });
  }, [token, conversationRefreshKey]);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 40);
    return () => clearTimeout(t);
  }, [messages.length, view, inDM]);

  function scrollToBottom() {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }

  // Helper: detect system DM (TeaKonn bot)
  function isSystemDMConversation(conv: any): boolean {
    try {
      if (!conv || conv.isGroup) return false;
      const users = (conv.participants || []).map((p: any) => ({
        username: String(p?.username || '').toLowerCase(),
        email: String(p?.email || '').toLowerCase(),
      }));
      const systemNames = ['teakonn'];
      const systemEmails = ['system@teakonn.com'];
      return users.some((u: any) => systemNames.includes(u.username) || systemEmails.includes(u.email));
    } catch {
      return false;
    }
  }

  // SEND MESSAGE -------------------------------------------------
  async function sendMessage(e?: React.FormEvent) {
    e?.preventDefault();
    if (!text && selectedImages.length === 0) return;
    if (sending) return;
    setSending(true);

    // Block replies to system DM (TeaKonn)
    if (inDM && activeConversation && isSystemDMConversation(activeConversation)) {
      setToast({ type: 'info', message: 'Replies to system messages are disabled.' });
      return;
    }

    const fileUrls: string[] = [];

    if (selectedImages.length > 0) {
      try {
        // Precompute placeholders with stable createdAt so receivers can reconcile later
        const targetRoom = inDM && activeConversation ? conversationRoomId(activeConversation) : room;
        const now = Date.now();
        const imageItems = selectedImages.map((img, idx) => ({
          createdAt: new Date(now + idx).toISOString(),
          textForThis: idx === 0 ? text : '',
        }));

        // Emit placeholders immediately to receivers for instant feedback
        imageItems.forEach((it) => {
          const placeholder = {
            _id: `temp-${now}-${Math.random()}`,
            sender: user,
            text: it.textForThis,
            room: targetRoom,
            fileUrl: '',
            createdAt: it.createdAt,
            isPending: true,
            isPlaceholder: true,
          };
          // Local optimistic already added below when we push after upload,
          // but emit to others right away so they see placeholders instantly
          try { socket.emit('temp_message', { room: targetRoom, message: placeholder }); } catch {}
        });

        if (selectedImages.length > 1) {
          // Use multi-file endpoint to reduce per-request overhead
          const fd = new FormData();
          selectedImages.forEach((img) => fd.append('files', img));
          // Show progress toast for batch upload
          setToast({ type: 'info', message: 'Uploading images… 0%' });
          const r = await axios.post(API + '/api/files/upload-multiple?folder=messages', fd, {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (pe) => {
              try {
                const total = pe.total || 0;
                const loaded = pe.loaded || 0;
                if (total > 0) {
                  const pct = Math.min(100, Math.round((loaded / total) * 100));
                  setToast({ type: 'info', message: `Uploading images… ${pct}%` });
                }
              } catch {}
            },
            maxBodyLength: Infinity,
            timeout: 120000,
          });
          const urls = Array.isArray(r.data?.urls) ? r.data.urls.map(String) : [];
          fileUrls.push(...urls.filter(Boolean));
          setToast({ type: 'success', message: 'Images uploaded' });
        } else {
          // Single-file upload path
          const fd = new FormData();
          fd.append('file', selectedImages[0]);
          setToast({ type: 'info', message: 'Uploading image… 0%' });
          const r = await axios.post(API + '/api/files/upload?folder=messages', fd, {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (pe) => {
              try {
                const total = pe.total || 0;
                const loaded = pe.loaded || 0;
                if (total > 0) {
                  const pct = Math.min(100, Math.round((loaded / total) * 100));
                  setToast({ type: 'info', message: `Uploading image… ${pct}%` });
                }
              } catch {}
            },
            maxBodyLength: Infinity,
            timeout: 120000,
          });
          const url = r.data?.url ? String(r.data.url) : '';
          if (url) fileUrls.push(url);
          setToast({ type: 'success', message: 'Image uploaded' });
        }
      } catch (e) {
        console.error('Image upload failed:', e);
        setToast({ type: 'error', message: 'Image upload failed' });
      }
    }

    const targetRoom = inDM && activeConversation ? conversationRoomId(activeConversation) : room;

    // Send message with all uploaded images
    if (fileUrls.length > 0) {
      for (let i = 0; i < fileUrls.length; i++) {
        const fileUrl = fileUrls[i];
        const createdAt = new Date(Date.now() + i).toISOString();
        const optimisticMessage = {
          _id: `temp-${Date.now()}-${Math.random()}`,
          sender: user,
          text: i === 0 ? text : '',
          room: targetRoom,
          fileUrl,
          createdAt,
          isPending: true,
        };

        // Add message immediately to UI (optimistic update)
        setMessages((m) => [...m, optimisticMessage]);

        socket.emit('send_message', {
          room: targetRoom,
          message: {
            sender: user,
            text: i === 0 ? text : '',
            room: targetRoom,
            fileUrl,
            createdAt,
            replyTo: replyTo?._id || null,
          },
        });
      }
    } else if (text) {
      const optimisticMessage = {
        _id: `temp-${Date.now()}-${Math.random()}`,
        sender: user,
        text,
        room: targetRoom,
        fileUrl: '',
        createdAt: new Date().toISOString(),
        isPending: true,
      };

      // Add message immediately to UI (optimistic update)
      setMessages((m) => [...m, optimisticMessage]);

      socket.emit('send_message', {
        room: targetRoom,
        message: {
          sender: user,
          text,
          room: targetRoom,
          fileUrl: '',
          createdAt: new Date().toISOString(),
          replyTo: replyTo?._id || null,
        },
      });
    }

    setText('');
    setSelectedImages([]);
    setFile(null);
    setReplyTo(null);

    // Reset textarea height
    setTimeout(() => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = '48px';
      }
    }, 0);

    scrollToBottom();

    // Refresh conversations if this was a DM (to update lastMessage and unread counts)
    if (inDM) {
      refreshConversations();
    }

    setSending(false);
  }
  // Message actions helpers ------------------------------------
  function copyMessageText(m: any) {
    try {
      if (m.text) navigator.clipboard.writeText(m.text);
      setOpenMessageActions(null);
    } catch {}
  }

  async function hideMessageForMe(id: string) {
    try {
      if (!confirm('Hide this message for you only?')) {
        return;
      }
      await axios.delete(`${API}/api/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((msgs) => msgs.filter((x) => x._id !== id));
    } catch (e) {
      console.error('Hide message error', e);
    } finally {
      setOpenMessageActions(null);
    }
  }

  async function deleteMessageForEveryone(id: string) {
    try {
      if (!confirm('Delete this message for everyone?')) {
        return;
      }

      // If the message is still pending (e.g., image placeholder), suppress future reconciliation
      const pendingMsg = messages.find((m) => m._id === id);
      if (pendingMsg && pendingMsg.isPending) {
        try {
          const senderId = String(pendingMsg.sender?._id || pendingMsg.sender || '');
          const ts = new Date(pendingMsg.createdAt || Date.now()).getTime();
          suppressedPendingRef.current = [
            ...suppressedPendingRef.current,
            { sender: senderId, ts },
          ];
        } catch {}
        // Remove locally immediately
        setMessages((msgs) => msgs.filter((x) => x._id !== id));
        setOpenMessageActions(null);
        return;
      }

      await axios.delete(`${API}/api/messages/${id}/force`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((msgs) => msgs.filter((x) => x._id !== id));
    } catch (e) {
      console.error('Force delete error', e);
      alert('Failed to delete message for everyone');
    } finally {
      setOpenMessageActions(null);
    }
  }

  // TYPING -------------------------------------------------------
  function onComposerChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value);

    const typingData = {
      room: inDM && activeConversation ? conversationRoomId(activeConversation) : room,
      userId: user?._id,
      user: { _id: user?._id, username: user?.username, avatar: user?.avatar },
      typing: !!e.target.value,
    };
    console.log('[Socket] Emitting typing:', typingData);
    socket.emit('typing', typingData);
  }

  // Handle Enter key press
  function handleKeyPress(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter') {
      // Shift+Enter always creates a new line
      if (e.shiftKey) {
        return; // Allow default behavior (new line)
      }

      if (enterToSend && !e.ctrlKey) {
        // Enter alone sends if setting is enabled
        e.preventDefault();
        sendMessage();
      } else if (!enterToSend && (e.ctrlKey || e.metaKey)) {
        // Ctrl+Enter or Cmd+Enter sends if setting is disabled
        e.preventDefault();
        sendMessage();
      }
      // Shift+Enter always allows new line (browser default)
    }
  }

  // IMAGE HANDLING -----------------------------------------------
  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedImages((prev) => [...prev, ...files]);
      setImagePreviewOpen(true);
    }
    e.target.value = ''; // Reset input
  }

  function removeImage(index: number) {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  }

  function clearAllImages() {
    setSelectedImages([]);
    setImagePreviewOpen(false);
  }

  // REACTIONS ----------------------------------------------------
  function reactionCount(msg: any, emoji: string) {
    return msg.reactions?.filter((r: any) => r.emoji === emoji).length || 0;
  }

  function hasReacted(msg: any, emoji: string) {
    const uid = String(user?._id || user?.id);
    return msg.reactions?.some((r: any) => r.userId === uid && r.emoji === emoji) || false;
  }

  function toggleReaction(msg: any, emoji: string) {
    const uid = String(user?._id || user?.id);

    // Optimistic update - update UI immediately
    setMessages((m) =>
      m.map((message) => {
        if (message._id !== msg._id) return message;

        const reactions = message.reactions || [];
        const myExisting = reactions.find((r: any) => r.userId === uid);

        // If clicking the emoji I already reacted with -> remove it.
        if (myExisting && myExisting.emoji === emoji) {
          const newReactions = reactions.filter(
            (r: any) => !(r.userId === uid && r.emoji === emoji),
          );
          return { ...message, reactions: newReactions };
        }

        // Otherwise, enforce single reaction per user: remove any prior and add the new one
        const withoutMine = reactions.filter((r: any) => r.userId !== uid);
        const newReactions = [...withoutMine, { userId: uid, emoji }];
        return { ...message, reactions: newReactions };
      }),
    );

    // Send to server
    socket.emit('react', {
      room: inDM && activeConversation ? conversationRoomId(activeConversation) : room,
      messageId: msg._id,
      userId: user?._id,
      emoji,
    });
  }

  // PROFILE FIXED FUNCTION --------------------------------------
  async function showProfile(userOrId: any) {
    if (!token) return;

    const id = typeof userOrId === 'string' ? userOrId : userOrId?._id || userOrId?.id;

    if (!id) return;

    // Open modal immediately to show loading state
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileUser(null);

    try {
      // This is the ONLY correct endpoint.
      const res = await axios.get(API + '/api/users/' + id, {
        headers: { Authorization: 'Bearer ' + token },
      });

      const u = res.data;
      setProfileUser(u);

      // followers & following come directly from backend
      setProfileFollowersCount(
        Array.isArray(u.followers)
          ? u.followers.length
          : typeof u.followers === 'number'
            ? u.followers
            : 0,
      );

      setProfileFollowingCount(
        Array.isArray(u.following)
          ? u.following.length
          : typeof u.following === 'number'
            ? u.following
            : 0,
      );

      // detect if THIS user follows them
      setProfileIsFollowed(
        Array.isArray(u.followers) ? u.followers.map(String).includes(String(user?._id)) : false,
      );
    } catch (err) {
      console.error('Profile load failed:', err);
    } finally {
      setProfileLoading(false);
    }
  }

  function editMyProfile() {
    setEditProfileOpen(true);
  }
  // FOLLOW / UNFOLLOW --------------------------------------------
  async function toggleFollowProfile() {
    if (!profileUser || !token) return;

    const id = profileUser._id;
    const following = profileIsFollowed;

    try {
      if (following) {
        await axios.post(
          API + '/api/users/' + id + '/unfollow',
          {},
          { headers: { Authorization: 'Bearer ' + token } },
        );

        setProfileIsFollowed(false);
        setProfileFollowersCount((c) => (c || 1) - 1);
      } else {
        await axios.post(
          API + '/api/users/' + id + '/follow',
          {},
          { headers: { Authorization: 'Bearer ' + token } },
        );

        setProfileIsFollowed(true);
        setProfileFollowersCount((c) => (c || 0) + 1);
      }
    } catch (e) {
      console.error('Follow error', e);
    }
  }
  // OPEN A DM FROM ANYWHERE --------------------------------------
  async function openConversation(conv: any) {
    const c = normalizeConversation(conv);
    const cid = conversationRoomId(c);
    setActiveConversation(c);
    setInDM(true);
    navigateTo('chat');

    if (socket.connected && cid) {
      console.log('[Socket] Opening conversation, joining room:', cid);
      socket.emit('join_room', cid);
    }

    // Optimistically zero unread for this conversation
    try {
      setConversations((list) => {
        if (!Array.isArray(list) || !cid) return list;
        return list.map((x: any) =>
          String(conversationRoomId(x)) === String(cid) ? { ...x, unreadCount: 0 } : x,
        );
      });
      if (cid) try { broadcastConversationRead(String(cid)); } catch {}
    } catch {}

    // Mark conversation as read (socket fast-path + REST)
    if (token && cid) {
      try {
        try { socket.emit('mark_conversation_read', { conversationId: cid }); } catch {}
        await axios.post(
          `${API}/api/conversations/${cid}/read`,
          {},
          { headers: { Authorization: 'Bearer ' + token } },
        );
        // Ensure sidebar badges refresh
        refreshConversations();
      } catch (err) {
        console.error('Failed to mark conversation as read:', err);
        // Still trigger a refresh attempt
        try { refreshConversations(); } catch {}
      }
    }
  }

  // START DM FROM PROFILE ----------------------------------------
  async function messageFromProfile() {
    if (!profileUser) return;

    try {
      const res = await axios.post(
        API + '/api/conversations',
        { partnerId: profileUser._id },
        { headers: { Authorization: 'Bearer ' + token } },
      );

      openConversation(res.data);
      setProfileOpen(false);
    } catch (e) {
      console.error('Could not start conversation', e);
      alert('Unable to start conversation');
    }
  }

  // Open Profile from global event/localStorage trigger (e.g., from Notifications page)
  useEffect(() => {
    function handleOpenProfile(ev: any) {
      try {
        const id = ev?.detail?.id || localStorage.getItem('auralink-open-profile');
        if (id) {
          showProfile(id);
          try { localStorage.removeItem('auralink-open-profile'); } catch {}
        }
      } catch {}
    }
    window.addEventListener('auralink-open-profile', handleOpenProfile as any);
    // Fallback on mount
    handleOpenProfile(null);
    return () => window.removeEventListener('auralink-open-profile', handleOpenProfile as any);
  }, [token]);

  // START DM WITH USER ID ----------------------------------------
  async function startConversationWithUser(userId: string) {
    console.log('[App] startConversationWithUser called with userId:', userId);
    console.log('[App] token exists:', !!token);

    if (!userId || !token) {
      alert('Please log in to send messages');
      return;
    }

    try {
      // Show a quick toast while opening chat
      setToast({ message: 'Opening chat…', type: 'info' });
      console.log('[App] Creating conversation with partnerId:', userId);
      const res = await axios.post(
        API + '/api/conversations',
        { partnerId: userId },
        { headers: { Authorization: 'Bearer ' + token } },
      );

      console.log('[App] Conversation created/fetched:', res.data);
      openConversation(res.data);
      // Optional success toast once chat is opened
      setToast({ message: 'Chat opened', type: 'success' });
    } catch (e) {
      console.error('[App] Could not start conversation', e);
      // Show error toast; keep alert for visibility
      setToast({ message: 'Unable to start conversation', type: 'error' });
      alert('Unable to start conversation');
    }
  }

  // OPEN DM WHEN COMING FROM OTHER VIEWS VIA localStorage KEY -----
  useEffect(() => {
    try {
      const pending = localStorage.getItem('auralink-open-chat-with');
      if (pending && token) {
        // Clear before starting to avoid repeated triggers
        localStorage.removeItem('auralink-open-chat-with');
        startConversationWithUser(String(pending));
      }
    } catch {}
  }, [view, token]);

  // SCROLL TO BOTTOM ---------------------------------------------
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 200;
    setShowScrollButton(!isNearBottom);
    // Hide unread marker once it has been scrolled into view/past
    try {
      if (unreadRef.current && messagesSectionRef.current) {
        const markerRect = unreadRef.current.getBoundingClientRect();
        const containerRect = messagesSectionRef.current.getBoundingClientRect();
        if (markerRect.top <= containerRect.top + 32) {
          setUnreadIndex(-1);
        }
      }
    } catch {}
  };

  // Compute unread separator position based on read receipts
  function computeUnreadIndex(list: any[]) {
    try {
      const uid = String(user?._id);
      const idx = list.findIndex(
        (m: any) => m.sender?._id !== uid && !(m.readBy || []).map(String).includes(uid),
      );
      setUnreadIndex(idx);
    } catch {
      setUnreadIndex(-1);
    }
  }

  // MESSAGE RENDERER ---------------------------------------------
  function renderMessages() {
    return messages.map((m, index) => {
      const date = dayjs(m.createdAt).format('YYYY-MM-DD');
      const prevDate = index > 0 ? dayjs(messages[index - 1].createdAt).format('YYYY-MM-DD') : null;
      const showDate = date !== prevDate;

      const showAvatar = shouldShowAvatar(index);
      const unreadMark = unreadIndex >= 0 && index === unreadIndex ? true : false;

      const senderStatus = statuses[String(m.sender?._id || m.sender?.id)] || null;

      return (
        <Fragment key={m._id}>
          {showDate && (
            <div className="my-6 text-center">
              <span className="px-4 py-1 rounded-full card-date">
                {date === dayjs().format('YYYY-MM-DD')
                  ? 'Today'
                  : date === dayjs().subtract(1, 'day').format('YYYY-MM-DD')
                    ? 'Yesterday'
                    : dayjs(m.createdAt).format('DD MMM YYYY')}
              </span>
            </div>
          )}

          {unreadMark && (
            <div ref={unreadRef} className="my-3 flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
              <span className="unread-pill">Unread</span>
              <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            </div>
          )}

            <div
            className={clsx(
              'flex items-start gap-3',
              String(m.sender?._id) === String(user?._id) && 'justify-end',
            )}
            style={{
              transition: 'opacity .25s, transform .25s',
              opacity: ready ? 1 : 0,
              transform: ready ? 'translateY(0)' : 'translateY(6px)',
            }}
            id={`msg-${m._id}`}
          >
            {messageSelectMode && (
              <div className="mt-1">
                <input
                  type="checkbox"
                  checked={!!selectedMessageIds[String(m._id)]}
                  onChange={() => toggleMessageSelected(String(m._id))}
                  aria-label="Select message"
                />
              </div>
            )}
            {String(m.sender?._id) !== String(user?._id) ? (
              showAvatar ? (
                <Avatar
                  src={makeAvatarUrl(m.sender?.avatar)}
                  className="avatar w-10 h-10 rounded-md object-cover"
                  alt={m.sender?.username || 'User'}
                />
              ) : (
                <div style={{ width: 40 }} />
              )
            ) : null}

            <div
              className={clsx('message', String(m.sender?._id) === String(user?._id) && 'msg-mine')}
              onClick={(e) => onMessageContainerClick(e, String(m._id))}
              // Long-press handlers removed
            >
              {/* Subtle logo inside bubble for brand and sizing */}
              <div className="absolute top-1 right-1 opacity-25 pointer-events-none hidden sm:block">
                <img src={tkLogo} alt="TeaKonn" className="w-4 h-4 rounded" />
              </div>
              <div className="flex items-center gap-2">
                {String(m.sender?._id) !== String(user?._id) && showAvatar && (
                  <strong>{m.sender?.username}</strong>
                )}
                <span
                  className="text-xs opacity-75"
                  title={dayjs(m.createdAt).format('dddd, DD MMM YYYY • HH:mm')}
                >
                  {dayjs(m.createdAt).format('HH:mm')}
                </span>

                {m.edited && <span className="text-xs opacity-50 ml-1">(edited)</span>}

                {/* Message Status Ticks - only show for sender */}
                {String(m.sender?._id) === String(user?._id) && (
                  <span className="ml-2 text-xs flex items-center gap-0.5">
                    {m.readBy && m.readBy.length > 0 ? (
                      // Double white tick for read (higher contrast on mine bubble)
                      <span className="text-white" title="Read">
                        ✓✓
                      </span>
                    ) : m.deliveredTo && m.deliveredTo.length > 0 ? (
                      // Double gray tick for delivered
                      <span className="text-gray-300" title="Delivered">
                        ✓✓
                      </span>
                    ) : (
                      // Single gray tick for sent
                      <span className="text-gray-400" title="Sent">
                        ✓
                      </span>
                    )}
                  </span>
                )}

                {/* Per-message status removed to reduce bubble clutter */}

                {/* Message options (three dots) aligned at end of header row */}
                <span className="ml-auto">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMessageActions((id) => (id === m._id ? null : m._id));
                    }}
                    className="p-1 rounded-md hover:opacity-80"
                    title="More"
                    aria-haspopup="menu"
                    aria-expanded={openMessageActions === m._id}
                  >
                    <MoreVertical className="w-5 h-5 text-theme-secondary" />
                  </button>
                </span>
              </div>

              {/* Reply context */}
              {m.replyTo && (
                <div
                  className="mt-2 p-2 rounded-md border text-xs opacity-90 cursor-pointer"
                  style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const pid = typeof m.replyTo === 'string' ? m.replyTo : m.replyTo?._id;
                    if (pid) {
                      const el = document.getElementById(`msg-${pid}`);
                      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  title="Jump to replied message"
                >
                  {(() => {
                    const pid = typeof m.replyTo === 'string' ? m.replyTo : m.replyTo?._id;
                    const parent: any =
                      (pid && messages.find((x: any) => x._id === pid)) || (m as any).replyTo;
                    const uname = parent?.sender?.username || 'User';
                    const ptext = (parent?.text || '').toString();
                    return (
                      <div className="flex items-center gap-2">
                        <span className="opacity-70">Replying to</span>
                        <strong>{uname}</strong>
                        {!!ptext && (
                          <span className="opacity-60 truncate max-w-[50%]">{ptext.slice(0, 100)}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {m.fileUrl && (
                <img
                  src={m.fileUrl.startsWith('http') ? m.fileUrl : API + m.fileUrl}
                  className="max-w-full w-auto h-auto rounded-md mt-2 cursor-zoom-in"
                  style={{ maxHeight: '400px', objectFit: 'contain' }}
                  onClick={() =>
                    openMessageImage(m.fileUrl.startsWith('http') ? m.fileUrl : API + m.fileUrl)
                  }
                />
              )}

              {editingMessageId === m._id ? (
                <div className="flex gap-2 items-start mt-2">
                  <input
                    className="input p-2 rounded-md flex-1"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                  />
                  <button className="btn" onClick={() => saveEdit(m._id)}>
                    Save
                  </button>
                  <button className="px-3 py-2 border rounded-md" onClick={cancelEdit}>
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  {m.text && (
                    <div
                      className="mt-2 break-words"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedMessages((prev) => ({ ...prev, [m._id]: !prev[m._id] }));
                      }}
                      style={
                        expandedMessages[m._id]
                          ? ({
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              cursor: 'auto',
                            } as any)
                          : ({
                              display: '-webkit-box',
                              WebkitLineClamp: 5,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              cursor: 'pointer',
                            } as any)
                      }
                      title={expandedMessages[m._id] ? undefined : 'Click to expand'}
                    >
                      {m.text}
                    </div>
                  )}
                  {m.text && m.text.length > 160 && (
                    <button
                      className="mt-1 text-xs text-cyan-600 dark:text-cyan-400 hover:opacity-80"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedMessages((prev) => ({ ...prev, [m._id]: !prev[m._id] }));
                      }}
                    >
                      {expandedMessages[m._id] ? 'See less' : 'See more'}
                    </button>
                  )}
                </>
              )}

              {/* Reactions */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {/* Existing reaction chips (hide chips with zero counts) */}
                {AVAILABLE_REACTIONS.map((e) => {
                  const count = reactionCount(m, e);
                  if (!count) return null;
                  const mine = hasReacted(m, e);
                  return (
                    <button
                      key={e}
                      className={clsx('reaction-chip', mine && 'mine')}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        toggleReaction(m, e);
                      }}
                      title={mine ? 'Remove your reaction' : 'React'}
                      aria-label={mine ? 'Remove your reaction' : 'React'}
                    >
                      <span className="emoji">{e}</span>
                      <span className="count">{count}</span>
                    </button>
                  );
                })}

                {/* Add reaction trigger */}
                <button
                  className="reaction-btn"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setOpenReactionPicker((prev) => ({ ...prev, [m._id]: !prev[m._id] }));
                  }}
                  aria-label="Add reaction"
                  title="Add reaction"
                >
                  + Add reaction
                </button>
              </div>

              {/* Inline reaction picker */}
              {openReactionPicker[m._id] && (
                <div
                  className="reaction-picker mt-2"
                  onClick={(e) => e.stopPropagation()}
                  role="menu"
                  aria-label="Pick a reaction"
                >
                  {AVAILABLE_REACTIONS.map((e) => (
                    <button
                      key={e}
                      className="reaction-option"
                      onClick={() => {
                        toggleReaction(m, e);
                        setOpenReactionPicker((prev) => ({ ...prev, [m._id]: false }));
                      }}
                      aria-label={`React with ${e}`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              )}

              {openMessageActions === m._id && (
                <>
                  {/* Overlay (visual only) — mobile full-screen, desktop only main area */}
                  <div className="fixed sm:absolute inset-0 z-40 bg-black/40" aria-hidden="true" />
                  {/* Responsive options menu: bottom sheet on mobile, popover on desktop */}
                  <div
                    className="fixed inset-x-4 bottom-4 z-50 rounded-2xl shadow-2xl border p-2 sm:absolute sm:top-20 sm:right-6 sm:left-auto sm:bottom-auto sm:w-80 sm:rounded-xl sm:shadow-xl"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-solid)' }}
                    onClick={(e) => e.stopPropagation()}
                    role="menu"
                    aria-label="Message options"
                  >
                    {/* Mobile drag handle */}
                    <div className="sm:hidden flex justify-center py-1">
                      <div className="w-10 h-1.5 rounded-full" style={{ background: 'var(--muted)' }} />
                    </div>
                    <div className="flex items-center justify-between px-2 pb-2 sm:hidden">
                      <div className="text-xs text-theme-secondary">Message Options</div>
                      <button
                        onClick={() => setOpenMessageActions(null)}
                        className="themed-card rounded-full p-1"
                        aria-label="Close options"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <button
                      onClick={() => {
                        setReplyTo(m);
                        setOpenMessageActions(null);
                        composerTextareaRef.current?.focus();
                      }}
                      className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:opacity-85"
                      role="menuitem"
                    >
                      Reply
                    </button>

                    {m.text && (
                      <button
                        onClick={() => copyMessageText(m)}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:opacity-85"
                        role="menuitem"
                      >
                        Copy text
                      </button>
                    )}

                    <button
                      onClick={() => hideMessageForMe(m._id)}
                      className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:opacity-85"
                      role="menuitem"
                    >
                      Hide for me
                    </button>

                    {String(m.sender?._id) === String(user?._id) && (
                      <>
                        <div className="border-t my-1" style={{ borderColor: 'var(--border)' }} />
                        <button
                          onClick={() => startEdit(m)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:opacity-85"
                          role="menuitem"
                        >
                          <Edit className="w-4 h-4" /> Edit
                        </button>
                        <button
                          onClick={() => deleteMessageForEveryone(m._id)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600 dark:text-red-400 rounded-lg hover:opacity-85"
                          role="menuitem"
                        >
                          <Trash2 className="w-4 h-4" /> Delete for everyone
                        </button>
                      </>
                    )}

                    <div className="mt-2 flex sm:hidden">
                      <button
                        onClick={() => setOpenMessageActions(null)}
                        className="w-full px-4 py-2 themed-card rounded-lg"
                        aria-label="Close"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </Fragment>
      );
    });
  }

  // Avatar upload via sidebar removed; use ProfileEditModal

  // LOGOUT -------------------------------------------------------
  function logout() {
    try {
      const preservePrefixes = [
        'auralink-notifs-seen:',
        'auralink-follow-date:',
        'auralink-followers-baseline:',
        'teakonn-theme',
      ];
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) || '';
        const keep = preservePrefixes.some((p) => k.startsWith(p));
        if (!keep) toRemove.push(k);
      }
      for (const k of toRemove) {
        try { localStorage.removeItem(k); } catch {}
      }
      // Reset unread counter on logout for safety
      try { localStorage.setItem('auralink-unread-notifs', '0'); } catch {}
    } catch {}
    setToken(null);
    setUser(null);
    window.location.reload();
  }

  // MESSAGE IMAGE VIEWER STATE ----------------------------------
  const [messageImageViewer, setMessageImageViewer] = React.useState<{ index: number } | null>(
    null,
  );
  const messageImageUrls = React.useMemo(() => {
    try {
      return messages
        .filter((m: any) => !!m.fileUrl)
        .map((m: any) => (m.fileUrl.startsWith('http') ? m.fileUrl : API + m.fileUrl));
    } catch {
      return [];
    }
  }, [messages]);
  function openMessageImage(url: string) {
    const idx = messageImageUrls.indexOf(url);
    setMessageImageViewer({ index: idx >= 0 ? idx : 0 });
  }
  function closeMessageImage() {
    setMessageImageViewer(null);
  }
  function prevMessageImage() {
    if (!messageImageUrls.length) return;
    setMessageImageViewer((v) => ({
      index: ((v?.index || 0) + messageImageUrls.length - 1) % messageImageUrls.length,
    }));
  }
  function nextMessageImage() {
    if (!messageImageUrls.length) return;
    setMessageImageViewer((v) => ({ index: ((v?.index || 0) + 1) % messageImageUrls.length }));
  }
  async function shareMessageImage() {
    try {
      const url = messageImageUrls[messageImageViewer?.index || 0];
      if ((navigator as any).share) {
        await (navigator as any).share({ title: 'TeaKonn Photo', url });
        return;
      }
      await navigator.clipboard.writeText(url);
      alert('Image link copied');
    } catch {}
  }
  async function downloadMessageImage() {
    const url = messageImageUrls[messageImageViewer?.index || 0];
    const filename = (url.split('/').pop() || 'image.jpg').split('?')[0];
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('fetch_failed');
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      try {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.click();
      } catch {}
    }
  }
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!messageImageViewer) return;
      if (e.key === 'Escape') closeMessageImage();
      else if (e.key === 'ArrowLeft') prevMessageImage();
      else if (e.key === 'ArrowRight') nextMessageImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [messageImageViewer, messageImageUrls]);

  // Pull-to-refresh: pull down from top to refresh the app
  const pullStartYRef = useRef<number | null>(null);
  const pullActiveRef = useRef<boolean>(false);
  const [pullRefreshing, setPullRefreshing] = useState<boolean>(false);
  useEffect(() => {
    const THRESHOLD = 70; // px
    function onTouchStart(e: TouchEvent) {
      try {
        if (window.scrollY <= 0) {
          pullStartYRef.current = e.touches[0]?.clientY || 0;
          pullActiveRef.current = false;
        } else {
          pullStartYRef.current = null;
          pullActiveRef.current = false;
        }
      } catch {}
    }
    function onTouchMove(e: TouchEvent) {
      try {
        if (pullStartYRef.current == null) return;
        const dy = (e.touches[0]?.clientY || 0) - pullStartYRef.current;
        if (dy > THRESHOLD) {
          pullActiveRef.current = true;
          setPullRefreshing(true);
        }
      } catch {}
    }
    function onTouchEnd() {
      try {
        if (pullActiveRef.current) {
          // Simple refresh: reload the page to refresh app state
          setTimeout(() => {
            setPullRefreshing(false);
            try {
              window.location.reload();
            } catch {}
          }, 100);
        } else {
          setPullRefreshing(false);
        }
      } catch {
        setPullRefreshing(false);
      } finally {
        pullStartYRef.current = null;
        pullActiveRef.current = false;
      }
    }
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart as any);
      window.removeEventListener('touchmove', onTouchMove as any);
      window.removeEventListener('touchend', onTouchEnd as any);
    };
  }, []);

  // AUTH CHECK ---------------------------------------------------
  if (!token || !user) {
    return authPage === 'login' ? (
      <Login
        onSuccess={({ token, user }) => {
          setToken(token);
          setUser(user);
        }}
        switchToRegister={() => setAuthPage('register')}
      />
    ) : (
      <Register
        onSuccess={({ token, user }) => {
          setToken(token);
          setUser(user);
        }}
        switchToLogin={() => setAuthPage('login')}
      />
    );
  }

  // ---- MAIN LAYOUT ----
  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
      {/* Email verification prompt moved to Edit Profile modal (non-blocking). */}
      {/* Onboarding one-time hints overlay */}
      {user && showOnboarding && (
        <OnboardingHints
          userId={String(user._id || user.id || '')}
          onDismiss={() => setShowOnboarding(false)}
        />
      )}
      {/* Unified Sidebar */}
      {token && (
        <Sidebar
          key={`${user?._id}-${user?.avatar}-${myStatus?.mood}-${myStatus?.emoji}-${JSON.stringify(user)}`}
          token={token}
          user={user}
          theme={theme}
          myStatus={myStatus}
          isOnline={user?._id ? onlineUsers.has(user._id) : false}
          onNavigate={(v) => {
            navigateTo(v as any);
            setInDM(false);
            setActiveConversation(null);
          }}
          onThemeToggle={toggleTheme}
          onLogout={logout}
          onStatusUpdated={onMyStatusUpdated}
          onShowProfile={showProfile}
          onEditProfile={editMyProfile}
          onOpenConversation={openConversation}
          /* Avatar upload props removed */
          conversations={conversations}
          makeAvatarUrl={makeAvatarUrl}
        />
      )}

      {/* ---------------- MAIN VIEW ---------------- */}
      <main
        className={clsx(
          'flex-1 flex flex-col pl-14 lg:pl-0',
          view === 'chat' ? 'overflow-hidden h-screen' : 'overflow-auto p-4 lg:p-6',
        )}
        style={{ color: 'var(--text)' }}
      >
        {/* Top brand bar with app icon (hidden in chat view) */}
        {view !== 'chat' && (
          <div className="mb-2 flex items-center justify-between px-2">
            <div className="flex items-center gap-2">
              <span className="font-bold">TeaKonn</span>
            </div>
            <div className="text-xs opacity-60">Welcome</div>
          </div>
        )}
        {view === 'dashboard' && (
          <ErrorBoundary fallback={<div className="p-4">Dashboard failed to load. Please try again.</div>}>
            <Dashboard
              token={token as any}
              onNavigate={(newView: string) => navigateTo(newView as any)}
            />
          </ErrorBoundary>
        )}
        {view === 'verify-request' && (
          token ? (
            <VerificationRequest token={token} onNavigate={(v: string) => navigateTo(v as any)} />
          ) : (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-2">Please log in</h2>
              <p className="text-sm text-theme-secondary">You need to be logged in to request verification.</p>
            </div>
          )
        )}
        {view === 'my-events' && (
          <MyEvents
            token={token as any}
            onNavigate={(newView: string) => navigateTo(newView as any)}
          />
        )}
        {view === 'my-services' && (
          <MyServices
            token={token as any}
            onNavigate={(newView: string) => navigateTo(newView as any)}
            onToast={(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => setToast({ message, type })}
          />
        )}
        {view === 'my-products' && (
          <MyProducts
            token={token as any}
            onNavigate={(newView: string) => navigateTo(newView as any)}
            onToast={(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => setToast({ message, type })}
          />
        )}

        {/* MY ACTIVITIES PAGE */}
        {view === 'my-activities' && (
          <MyActivities
            token={token as any}
            onNavigate={(newView: string) => navigateTo(newView as any)}
            onOpenConversation={(c: any) => openConversation(c)}
            onToast={(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => setToast({ message, type })}
          />
        )}

        {/* DISCOVER PAGE */}
        {view === 'discover' && (
          <ErrorBoundary fallback={<div className="p-4">Discover failed to load. Please try again.</div>}>
            <Discover
              token={token}
              onViewProfile={showProfile}
              onStartConversation={startConversationWithUser}
            />
          </ErrorBoundary>
        )}
        {view === 'discover-venues' && (
          <DiscoverVenues token={token} onBack={() => navigateTo('discover')} />
        )}

        {/* PAST EVENTS PAGE */}
        {view === 'past-events' && (
          <PastEvents
            token={token as any}
            onBack={() => navigateTo('dashboard')}
            onNavigate={(v: string) => navigateTo(v as any)}
          />
        )}

        {/* OTHER EVENTS PAGE */}
        {view === 'other-events' && (
          <OtherEvents
            token={token as any}
            onBack={() => navigateTo('dashboard')}
            onNavigate={(v: string) => navigateTo(v as any)}
          />
        )}

        {/* SPORTS EVENTS PAGE (simplified booking flow) */}
        {view === 'sports' && (
          <SportsEvents
            token={token}
            onViewProfile={showProfile}
            onStartConversation={startConversationWithUser}
          />
        )}

        {/* FOLLOWERS PAGE */}
        {view === 'followers' && (
          <FollowersList
            token={token}
            currentUserId={user?._id}
            onShowProfile={showProfile}
            onOpenConversation={openConversation}
          />
        )}

        {/* FOLLOWING PAGE */}
        {view === 'following' && (
          <FollowingList
            token={token}
            currentUserId={user?._id}
            onShowProfile={showProfile}
            onOpenConversation={openConversation}
          />
        )}

        {/* NOTIFICATIONS PAGE */}
        {view === 'notifications' && (
          <Notifications
            token={token}
            onBack={() => navigateTo('dashboard')}
          />
        )}

        {/* ALL USERS PAGE */}
        {view === 'all-users' && (
          <AllUsers
            token={token}
            currentUserId={user?._id}
            onOpenConversation={(c) => openConversation(c)}
            onShowProfile={showProfile}
          />
        )}

        {/* ADMIN PAGE */}
        {view === 'admin' && (
          (user?.role === 'admin' || user?.role === 'super_admin') ? (
            <AdminDashboard onNavigate={(v) => navigateTo(v as any)} />
          ) : (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-2">Unauthorized</h2>
              <p className="text-sm text-theme-secondary">You do not have access to this page.</p>
            </div>
          )
        )}

        {view === 'admin-stats' && (
          (user?.role === 'admin' || user?.role === 'super_admin') ? (
            <AdminStats />
          ) : (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-2">Unauthorized</h2>
              <p className="text-sm text-theme-secondary">You do not have access to this page.</p>
            </div>
          )
        )}

        {view === 'admin-users' && (
          (user?.role === 'admin' || user?.role === 'super_admin') ? (
            <AdminUsers />
          ) : (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-2">Unauthorized</h2>
              <p className="text-sm text-theme-secondary">You do not have access to this page.</p>
            </div>
          )
        )}

        {view === 'admin-flags' && (
          (user?.role === 'admin' || user?.role === 'super_admin') ? (
            <AdminFlags />
          ) : (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-2">Unauthorized</h2>
              <p className="text-sm text-theme-secondary">You do not have access to this page.</p>
            </div>
          )
        )}

        {view === 'admin-posts' && (
          (user?.role === 'admin' || user?.role === 'super_admin') ? (
            <AdminPosts />
          ) : (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-2">Unauthorized</h2>
              <p className="text-sm text-theme-secondary">You do not have access to this page.</p>
            </div>
          )
        )}

        {view === 'admin-verifications' && (
          (user?.role === 'admin' || user?.role === 'super_admin') ? (
            <AdminVerificationRequests token={token || undefined} onNavigate={(v: string) => navigateTo(v as any)} />
          ) : (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-2">Unauthorized</h2>
              <p className="text-sm text-theme-secondary">You do not have access to this page.</p>
            </div>
          )
        )}

        {/* ROOMS PAGE */}
        {view === 'posts' && (
          <Posts
            token={token}
            currentUserId={user?._id}
            onShowProfile={(u: any) => showProfile(u)}
            onNavigate={(v: string) => navigateTo(v as any)}
          />
        )}

        {view === 'user-content' && (
          <UserContent token={token} onNavigate={(v: string) => navigateTo(v as any)} />
        )}

        {/* DIRECT MESSAGES PAGE */}
        {view === 'direct-messages' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">💬 Direct Messages</h2>
            <ConversationsList
              key={conversationRefreshKey}
              token={token}
              currentUserId={user?._id}
              onShowProfile={(u: any) => showProfile(u)}
              onOpenConversation={(c: any) => openConversation(c)}
              onlineUsers={onlineUsers}
            />
          </div>
        )}

        {/* GROUP CHATS PAGE */}
        {view === 'group-chats' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">👥 Group Chats</h2>
            <GroupChatsList
              token={token}
              onOpenRoom={(rid: string) => {
                try { localStorage.setItem('auralink-open-room', rid); } catch {}
                navigateTo('chat');
              }}
            />
          </div>
        )}

        {/* CHAT / DM PAGE */}
        {view === 'chat' && (
          <div className="h-full grid grid-rows-[auto_1fr_auto]">
            <header
              className="flex flex-wrap items-center justify-between gap-2 p-4 border-b relative z-20"
              style={{ borderColor: 'var(--border)', background: 'var(--card-solid)', overflow: 'visible' }}
            >
              {inDM && activeConversation ? (
                <div className="flex items-center gap-3">
                  {(() => {
                    const partner = (activeConversation.participants || []).find(
                      (p: any) => String(p._id) !== String(user?._id),
                    );
                    return (
                      <>
                        <div className="relative">
                          <Avatar
                            src={makeAvatarUrl(partner?.avatar)}
                            className="w-10 h-10 rounded-md object-cover"
                            alt={partner?.username || 'User'}
                          />
                          {/* Online status indicator */}
                          {partner?._id && onlineUsers.has(partner._id) && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white dark:border-slate-800 rounded-full shadow-sm"></div>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{partner?.username}</span>
                            {/* Three-dots icon to open chat actions (match posts) */}
                            <button
                              className="p-1 rounded-md border hover:bg-slate-100 dark:hover:bg-slate-800"
                              style={{ borderColor: 'var(--border)' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowChatActions(true);
                              }}
                              title="More"
                              aria-haspopup="menu"
                              aria-expanded={showChatActions}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {partner?._id && onlineUsers.has(partner._id) && (
                              <span className="text-xs text-green-500 font-medium">● Online</span>
                            )}
                          </div>
                          {/* Show typing indicator for this specific user */}
                          {partner?._id && typingUsers[partner._id] ? (
                            <div className="flex items-center gap-1.5 text-cyan-500 text-xs">
                              <div className="flex gap-0.5">
                                <span
                                  className="w-1 h-1 bg-cyan-500 rounded-full animate-bounce"
                                  style={{ animationDelay: '0ms' }}
                                ></span>
                                <span
                                  className="w-1 h-1 bg-cyan-500 rounded-full animate-bounce"
                                  style={{ animationDelay: '150ms' }}
                                ></span>
                                <span
                                  className="w-1 h-1 bg-cyan-500 rounded-full animate-bounce"
                                  style={{ animationDelay: '300ms' }}
                                ></span>
                              </div>
                              <span>typing...</span>
                            </div>
                          ) : (
                            <div className="text-xs opacity-70">Private conversation</div>
                          )}
                          {/* Partner status below 'Private conversation' */}
                          {(() => {
                            const ps = partner?._id
                              ? statuses[String(partner._id)]
                              : null;
                            return ps ? (
                              <div className="mt-1 text-xs px-2 py-0.5 card-status rounded-md inline-flex items-center gap-1">
                                <span>{ps.emoji}</span>
                                <span className="opacity-80">{ps.mood}</span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="flex flex-col">
                  <h3 className="text-lg font-semibold">{roomTitle || `#${room}`}</h3>
                  {roomSubtitle && (
                    <span className="text-xs opacity-75">{roomSubtitle}</span>
                  )}
                </div>
              )}

              <div className="text-sm flex flex-wrap items-center gap-2">
                {Object.keys(typingUsers).length > 0 && (
                  <div className="flex items-center gap-1.5 text-cyan-500 animate-pulse">
                    <div className="flex gap-0.5">
                      <span
                        className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      ></span>
                      <span
                        className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      ></span>
                      <span
                        className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      ></span>
                    </div>
                    <span className="text-xs font-medium">
                      {Object.keys(typingUsers).length === 1
                        ? `${Object.values(typingUsers)[0]?.username || 'Someone'} is typing...`
                        : Object.keys(typingUsers).length === 2
                          ? `${Object.values(typingUsers)
                              .map((u: any) => u?.username || 'Someone')
                              .join(' and ')} are typing...`
                          : `${Object.keys(typingUsers).length} people are typing...`}
                    </span>
                  </div>
                )}
                {/* Removed jump-to-unread button; auto-scroll to latest on open */}

                {/* Long-press chat actions sheet */}
                {showChatActions ? (
                  <div className="relative">
                    <div
                      className="fixed inset-0 z-[80] bg-transparent"
                      onClick={(e) => { e.stopPropagation(); setShowChatActions(false); }}
                      aria-hidden="true"
                    ></div>
                    <div
                      className="box fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] w-[92vw] max-w-sm rounded-2xl shadow-2xl border p-2 sm:absolute sm:bottom-auto sm:left-auto sm:right-0 sm:top-8 sm:w-64 sm:rounded-xl sm:shadow-xl"
                      style={{ borderColor: 'var(--border)', background: 'var(--card-solid)', opacity: 1, backdropFilter: 'none', filter: 'none' }}
                      onClick={(e) => e.stopPropagation()}
                      role="menu"
                      aria-label="Chat options"
                    >
                      <div className="flex items-center justify-between px-2 pb-2">
                        <div className="text-xs text-theme-secondary">Chat Options</div>
                        <button
                          onClick={() => setShowChatActions(false)}
                          className="rounded-full p-1 border"
                          style={{ background: 'var(--card-solid)', borderColor: 'var(--border)' }}
                          aria-label="Close options"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={toggleEnterToSend}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        role="menuitem"
                      >
                        <Zap className={clsx('w-4 h-4', enterToSend ? 'text-indigo-500' : 'text-gray-400')} />
                        <div className="flex-1">
                          <div className="font-medium">Send on Enter</div>
                          <div className="text-xs opacity-70">
                            {enterToSend ? 'Enabled (Press Enter)' : 'Disabled (Press Ctrl+Enter)'}
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => { setMessageSelectMode((v) => !v); setShowChatActions(false); }}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        role="menuitem"
                      >
                        <MoreVertical className="w-4 h-4" />
                        <div className="flex-1">
                          <div className="font-medium">Select Messages</div>
                          <div className="text-xs opacity-70">Multi-select for bulk actions</div>
                        </div>
                      </button>
                      <button
                        onClick={markAllRead}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        role="menuitem"
                      >
                        <span className="w-4 h-4">✓✓</span>
                        <div className="flex-1">
                          <div className="font-medium">Mark All Read</div>
                          <div className="text-xs opacity-70">Set all messages as read</div>
                        </div>
                      </button>
                      {inDM && activeConversation ? (
                        <button
                          onClick={markConversationUnread}
                          className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                          role="menuitem"
                        >
                          <span className="w-4 h-4">•</span>
                          <div className="flex-1">
                            <div className="font-medium">Mark Conversation Unread</div>
                            <div className="text-xs opacity-70">Reset read position</div>
                          </div>
                        </button>
                      ) : null}
                      <button
                        onClick={async () => {
                          if (!confirm('Clear all messages in this chat? This cannot be undone.')) return;
                          try {
                            if (inDM && activeConversation) {
                              await axios.delete(
                                `${API}/api/messages/room/${conversationRoomId(activeConversation)}/clear`,
                                { headers: { Authorization: `Bearer ${token}` } },
                              );
                            } else {
                              await axios.delete(`${API}/api/messages/room/${room}/clear`, { headers: { Authorization: `Bearer ${token}` } });
                            }
                            setMessages([]);
                            setShowChatActions(false);
                          } catch (e) {
                            console.error('Clear chat error', e);
                            alert('Failed to clear chat');
                          }
                        }}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        role="menuitem"
                      >
                        <Trash2 className="w-4 h-4 text-orange-500" />
                        <div>
                          <div className="font-medium">Clear Chat</div>
                          <div className="text-xs opacity-70">Remove all messages</div>
                        </div>
                      </button>
                      {inDM && activeConversation ? (
                        <div>
                          <div className="border-t my-1" style={{ borderColor: 'var(--border)' }}></div>
                          <button
                            onClick={async () => {
                              if (!confirm('Delete this entire conversation permanently? This cannot be undone.')) return;
                              try {
                                await axios.delete(`${API}/api/conversations/${conversationRoomId(activeConversation)}`, { headers: { Authorization: `Bearer ${token}` } });
                                setActiveConversation(null);
                                setInDM(false);
                                setMessages([]);
                                setView('direct-messages');
                                const res = await axios.get(`${API}/api/conversations`, { headers: { Authorization: `Bearer ${token}` } });
                                setConversations(res.data || []);
                              } catch (e) {
                                console.error('Delete conversation error', e);
                                alert('Failed to delete conversation');
                              } finally {
                                setShowChatActions(false);
                              }
                            }}
                            className="flex items-center gap-2 w-full px-4 py-3 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                            role="menuitem"
                          >
                            <X className="w-4 h-4 text-red-500" />
                            <div>
                              <div className="font-medium text-red-500">Delete Conversation</div>
                              <div className="text-xs opacity-70">Permanently remove chat</div>
                            </div>
                          </button>
                        </div>
                      ) : null}
                      <div className="mt-2 flex">
                        <button
                          onClick={() => setShowChatActions(false)}
                          className="w-full px-4 py-2 border rounded-lg"
                          style={{ background: 'var(--card-solid)', borderColor: 'var(--border)' }}
                          aria-label="Close"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </header>
            {/* Long-press hint removed for clarity and consistency */}

            {/* MESSAGE LIST */}
            <section
              ref={messagesSectionRef}
              className="overflow-y-auto p-4 relative"
              onScroll={handleScroll}
            >
              {messageSelectMode && (
                <div className="sticky top-0 z-20 mb-2 flex items-center gap-2 p-2 rounded-md border overflow-x-auto whitespace-nowrap" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
                  <span className="text-xs">Selection</span>
                  <button className="text-[11px] px-2 py-1 rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={selectAllMessages}>Select all</button>
                  <button className="text-[11px] px-2 py-1 rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={clearSelectedMessages}>Clear</button>
                  <button className="text-[11px] px-2 py-1 rounded-md border text-red-600" style={{ borderColor: 'var(--border)' }} onClick={deleteSelectedMessagesForMe}>Delete</button>
                  <button className="ml-auto text-[11px] px-2 py-1 rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={() => setMessageSelectMode(false)}>Done</button>
                </div>
              )}
              <div className="flex flex-col gap-4">
                {messagesLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-md"
                          style={{ background: 'var(--muted)' }}
                        />
                        <div className="flex-1">
                          <div
                            className="h-3 w-24 rounded"
                            style={{ background: 'var(--muted)' }}
                          />
                          <div
                            className="mt-2 h-3 w-3/5 rounded"
                            style={{ background: 'var(--muted)' }}
                          />
                          <div
                            className="mt-2 h-3 w-2/5 rounded"
                            style={{ background: 'var(--muted)' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  renderMessages()
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Scroll to Bottom Button */}
              {showScrollButton && (
                <button
                  onClick={scrollToBottom}
                  className="fixed bottom-24 right-8 bg-gradient-to-r from-cyan-400 to-purple-500 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10"
                  title="Scroll to bottom"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              )}
            </section>

            {/* MESSAGE COMPOSER */}
            <form
              className="composer flex flex-col gap-2 p-4 border-t rounded-t-xl shadow-sm"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              onSubmit={sendMessage}
            >
              {replyTo && (
                <div
                  className="mb-2 p-2 rounded-md border text-xs flex items-center gap-2"
                  style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                >
                  <span className="opacity-80">Replying to</span>
                  <strong>{replyTo?.sender?.username || 'User'}</strong>
                  <span className="opacity-70 truncate max-w-[50%]">
                    {(replyTo?.text || '').slice(0, 80)}
                  </span>
                  <button
                    type="button"
                    className="ml-auto text-[11px] px-2 py-1 rounded-md border"
                    style={{ borderColor: 'var(--border)' }}
                    onClick={() => setReplyTo(null)}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {/* Image Preview Bar */}
              {selectedImages.length > 0 && (
                <div className="flex gap-2 p-2 bg-slate-800/30 rounded-md overflow-x-auto">
                  {selectedImages.map((img, idx) => (
                    <div key={idx} className="relative flex-shrink-0">
                      <img
                        src={URL.createObjectURL(img)}
                        alt={`Preview ${idx + 1}`}
                        className="w-20 h-20 object-cover rounded-md cursor-pointer"
                        onClick={() => setImagePreviewOpen(true)}
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-3">
                <textarea
                  className="input composer-input w-full pt-3 pl-3 pr-3 pb-3 rounded-2xl resize-none overflow-y-auto overflow-x-hidden leading-6"
                  style={{
                    minHeight: '48px',
                    maxHeight: '144px',
                    height: '48px',
                  }}
                  wrap="soft"
                  ref={composerTextareaRef}
                  value={text}
                  onChange={onComposerChange}
                  onKeyDown={handleKeyPress}
                  placeholder={
                    inDM
                      ? enterToSend
                        ? 'Message... (Enter to send)'
                        : 'Message... (Ctrl+Enter to send)'
                      : enterToSend
                        ? 'Say something... (Enter to send)'
                        : 'Say something... (Ctrl+Enter to send)'
                  }
                  disabled={inDM && activeConversation && isSystemDMConversation(activeConversation)}
                  rows={1}
                  onInput={(e: any) => {
                    // Auto-resize textarea as user types - expand when scrollHeight exceeds current height
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = '48px';
                    if (target.scrollHeight > 48) {
                      target.style.height = Math.min(target.scrollHeight, 144) + 'px';
                    }
                  }}
                />
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {/* Image Button with Icon */}
                  <label className="cursor-pointer composer-icon-btn">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-theme-secondary"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </label>

                  <button
                    className="composer-send-btn w-full sm:w-auto mt-2 sm:mt-0"
                    type="submit"
                    title="Send message"
                    disabled={sending || (inDM && activeConversation && isSystemDMConversation(activeConversation))}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </main>

      {profileOpen && profileUser && (
        <UserProfileModal
          user={{ ...profileUser, isFollowed: profileIsFollowed }}
          visible={profileOpen}
          onClose={() => setProfileOpen(false)}
          token={token}
          onOpenConversation={(u: any) => startConversationWithUser(u.id || u._id)}
          currentUserId={user?.id || user?._id}
          onNavigate={(v: string) => navigateTo(v as any)}
        />
      )}

      {editProfileOpen && user && (
        <ProfileEditModal
          visible={editProfileOpen}
          user={user}
          onClose={() => setEditProfileOpen(false)}
          onUpdated={(u) => {
            setUser(u);
            try { localStorage.setItem('user', JSON.stringify(u)); } catch {}
          }}
        />
      )}

      {/* ---------------- IMAGE PREVIEW MODAL ---------------- */}
      {imagePreviewOpen && selectedImages.length > 0 && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={() => setImagePreviewOpen(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg p-6 w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {selectedImages.length} Image{selectedImages.length > 1 ? 's' : ''} Selected
              </h3>
              <button
                onClick={() => setImagePreviewOpen(false)}
                className="text-2xl opacity-60 hover:opacity-100"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              {selectedImages.map((img, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={URL.createObjectURL(img)}
                    alt={`Image ${idx + 1}`}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => removeImage(idx)}
                      className="bg-red-500 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                    {img.name}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={clearAllImages} className="px-4 py-2 border rounded-md">
                Clear All
              </button>
              <button onClick={() => setImagePreviewOpen(false)} className="btn px-4 py-2">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- MESSAGE IMAGE LIGHTBOX ---------------- */}
      {messageImageViewer && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeMessageImage}
        >
          <button
            onClick={closeMessageImage}
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
            aria-label="Close image preview"
          >
            <X className="w-6 h-6" />
          </button>
          {messageImageUrls.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevMessageImage();
                }}
                className="absolute left-4 mid:top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 rounded-full p-3"
              >
                ‹
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextMessageImage();
                }}
                className="absolute right-4 mid:top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 rounded-full p-3"
              >
                ›
              </button>
            </>
          )}
          <img
            src={messageImageUrls[messageImageViewer.index]}
            alt="Message Image"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                shareMessageImage();
              }}
              className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm"
            >
              Share
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadMessageImage();
              }}
              className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm"
            >
              Download
            </button>
          </div>
        </div>
      )}
      {/* Global notification toast (alerts on login/offline or realtime) */}
      {toast && (
        <NotificationToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {/* AI Assistant Widget */}
      {ASSISTANT_ENABLED && <AssistantWidget token={token} user={user as any} view={view} />}
    </div>
  );
}

// Trigger onboarding hints when eligible and not dismissed
// Place outside component-level hooks is not valid; use inside App component
